import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Sortable.js drag-and-drop controller
// Replaces jQuery UI Sortable with modern, touch-enabled drag-and-drop
//
// Usage:
//   ul data-controller="sortable"
//      data-sortable-handle-value=".handle"
//      data-sortable-url-value="/admin/reorder"
//      data-sortable-scope-value="data_field"
//      data-sortable-item-selector-value=".item"
//
// Cross-list dragging:
//   When two or more sortables on the same page share the same `scope` value
//   AND each sets `parent-id-value`, items can be dragged between them. The
//   moved item's `[parent_id_name]` hidden input is rewritten to the new
//   list's `parent-id-value` before the destination POST.
//
// Drop-zone pattern:
//   A list with `drop-into-selector-value` acts as a forwarder: items dropped
//   on it are moved into the referenced target list (appended at the end),
//   then the target list's positions are recomputed and persisted. Useful for
//   accepting drops on a collapsed-card header without expanding the card.
//
export default class extends Controller {
  static values = {
    handle: String,
    animation: { type: Number, default: 150 },
    url: String,
    scope: String,
    parentId: String,
    parentIdName: { type: String, default: "data_field_group_id" },
    dropIntoSelector: String,
    itemSelector: { type: String, default: ".item" }
  }

  connect() {
    // Check if we're inside an element that's being dragged
    // If so, DON'T initialize sortable yet - wait until drag completes
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      this.shouldInitializeOnNextConnect = true
      return
    }

    // If we were skipped before and reconnecting after drag, initialize now
    if (this.shouldInitializeOnNextConnect) {
      this.shouldInitializeOnNextConnect = false
    }

    // Sortable is loaded globally via CDN in application.slim
    if (typeof Sortable === 'undefined') {
      logger.error('Sortable.js library not loaded')
      return
    }

    this.initializeSortable()
  }

  disconnect() {
    // Check if we're inside an element that's being dragged
    // If so, DON'T destroy sortable - parent drag is in progress
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      return
    }

    // DON'T destroy sortable if we're being disconnected because we skipped init during drag
    if (this.shouldInitializeOnNextConnect) {
      return
    }

    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  initializeSortable() {
    const config = {
      animation: this.animationValue,
      handle: this.hasHandleValue ? this.handleValue : null,
      onEnd: this.handleSortEnd.bind(this),
      onAdd: this.handleSortAdd.bind(this),
      onStart: (evt) => {
        // Mark the dragged item so nested controllers know not to reinitialize
        evt.item.dataset.sortableDragging = 'true'
        // Mark <body> with the dragged scope so CSS can reveal drop-zones for
        // matching scopes only (e.g. dragging a field shouldn't light up
        // group-level drop-zones).
        document.body.dataset.sortableDragging = this.hasScopeValue ? this.scopeValue : 'true'

        // Prevent Bootstrap collapse from interfering with drag
        const collapseToggles = evt.item.querySelectorAll('[data-bs-toggle="collapse"]')
        collapseToggles.forEach(toggle => {
          toggle.style.pointerEvents = 'none'
        })
      }
    }

    // Add drag class for visual feedback
    config.dragClass = 'sortable-drag'
    config.ghostClass = 'sortable-ghost'
    config.chosenClass = 'sortable-chosen'

    // Filter for items if specified
    if (this.hasItemSelectorValue) {
      config.draggable = this.itemSelectorValue
    }

    // Cross-list dragging: opt-in by setting `scope` AND `parent-id-value`
    // (or by being a drop-zone forwarder via `drop-into-selector-value`).
    // Sortables sharing the same `scope` interchange items; lists without a
    // parent-id stay isolated under a per-instance random group name.
    //
    // `put` MUST be restricted to the scope name (not `true`). SortableJS
    // treats `put: true` as "accept items from any group regardless of name",
    // which would let a higher-level list (e.g. groups in the schema editor)
    // be dropped into a child list (e.g. fields inside one of those groups),
    // silently re-parenting the moved item's children. `put: [scopeValue]`
    // requires a name match, restoring the intended scope isolation.
    //
    // Non-cross-list sortables also need `pull: false, put: false` to lock
    // them — without it, SortableJS's defaults still let items be dragged
    // out into any list whose `put` accepts them.
    const canCrossList = this.hasScopeValue && (this.hasParentIdValue || this.hasDropIntoSelectorValue)
    config.group = canCrossList
      ? { name: this.scopeValue, pull: true, put: [this.scopeValue] }
      : { name: `sortable-${Math.random().toString(36).slice(2)}`, pull: false, put: false }

    // Initialize Sortable.js
    this.sortable = Sortable.create(this.element, config)
  }

  handleSortEnd(evt) {
    // Remove the dragging marker so nested controllers can reinitialize
    delete evt.item.dataset.sortableDragging
    delete document.body.dataset.sortableDragging

    // Re-enable collapse toggles after drag
    const collapseToggles = evt.item.querySelectorAll('[data-bs-toggle="collapse"]')
    collapseToggles.forEach(toggle => {
      toggle.style.pointerEvents = ''
    })

    // Drop-zone forwarders never persist their own state — the target list
    // already saved during onAdd. Skip both the position rewrite and the POST.
    if (this.hasDropIntoSelectorValue) {
      return
    }

    // Cross-list move: onEnd fires on the *source* list. The destination
    // already saved during onAdd, and acts_as_list auto-compacts the source
    // scope when the moved item's parent_id changes. A second POST from the
    // source would race the destination's POST and corrupt positions.
    if (evt && evt.from && evt.to && evt.from !== evt.to && evt.from === this.element) {
      return
    }

    // Within-list reorder: rewrite positions and persist.
    this.updatePositions()

    // If URL is provided, POST the updated data immediately
    if (this.hasUrlValue && this.hasScopeValue) {
      this.saveOrder()
    }
  }

  // Cross-list drop landed in this list. SortableJS only fires `onEnd` on the
  // source list, so the destination would never persist its new parent-id
  // assignment without this hook.
  handleSortAdd(evt) {
    // Drop-zone forwarder: relocate the dropped item into the real target
    // list and let that controller handle the save.
    if (this.hasDropIntoSelectorValue) {
      const target = document.querySelector(this.dropIntoSelectorValue)
      if (!target) {
        logger.error(`Sortable drop-into target not found: ${this.dropIntoSelectorValue}`)
        return
      }
      target.appendChild(evt.item)
      const targetCtrl = this.application.getControllerForElementAndIdentifier(target, this.identifier)
      if (targetCtrl) {
        targetCtrl.updatePositions()
        if (targetCtrl.hasUrlValue && targetCtrl.hasScopeValue) {
          targetCtrl.saveOrder()
        }
      }
      return
    }

    // Regular cross-list drop: rewrite parent-id on the moved item and save.
    // The source list will run its own updatePositions + saveOrder in onEnd.
    this.updatePositions()
    if (this.hasUrlValue && this.hasScopeValue) {
      this.saveOrder()
    }
  }

  updatePositions() {
    const items = this.element.querySelectorAll(this.itemSelectorValue)
    const scope = this.scopeValue

    items.forEach((item, index) => {
      // Update position input
      const positionInput = item.querySelector(`input[name^="${scope}"][name$="[position]"]`)
      if (positionInput) {
        positionInput.value = index
      }

      // Rewrite the parent-id hidden input on every item so cross-list drops
      // persist the new parent. Within-list reorders write the same value back
      // (no-op on the server).
      if (this.hasParentIdValue) {
        const parentSuffix = `[${this.parentIdNameValue}]`
        const parentInput = item.querySelector(`input[name^="${scope}"][name$="${parentSuffix}"]`)
        if (parentInput) {
          parentInput.value = this.parentIdValue
        }
      }
    })
  }

  saveOrder() {
    // Serialize all inputs with the scope prefix
    const inputs = this.element.querySelectorAll(`input[name^="${this.scopeValue}"]`)
    const formData = new FormData()

    inputs.forEach(input => {
      if (input.name && input.value !== undefined) {
        formData.append(input.name, input.value)
      }
    })

    // POST to server. Prefer turbo-stream so endpoints can react to a reorder
    // by updating sibling UI (e.g. empty-state placeholders, action buttons
    // that gate on collection size). Falls back to JSON for endpoints that
    // don't render streams — both formats stay supported.
    fetch(this.urlValue, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html, application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to save order: ${response.status} ${response.statusText}`)
      }
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('turbo-stream')) {
        return response.text().then(html => {
          if (html) Turbo.renderStreamMessage(html)
        })
      }
      return response.json()
    })
    .then(() => {
      logger.log('Order saved successfully')
    })
    .catch(error => {
      logger.error('Error saving order:', error)
      // Could show user notification here if needed
    })
  }
}
